import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, MaskLine, Overline, Cta } from "@/components/site/ui";
import {
  Ribbon,
  ServicesGrid,
  WhyChoose,
  ProcessSection,
  StatsBar,
  Testimonials,
  FaqSection,
  CtaBand,
} from "@/components/site/sections";
import { LeadForm } from "@/components/site/LeadForm";
import { IMAGES, MANIFESTO } from "@/data/content";
import { easing } from "@/components/site/ui";
import { MapPin } from "lucide-react";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#1C1A17]">
      <motion.img
        src={IMAGES.heroLiving}
        alt="Luxury living room interior by Spherio Living"
        style={{ y, scale }}
        className="absolute inset-0 h-full w-full object-cover opacity-60"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1C1A17] via-[#1C1A17]/40 to-[#1C1A17]/60" />

      <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-col justify-end pb-16 lg:pb-24">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
          <Overline dark>Spherio Living — Home Interiors & Modular Design</Overline>
        </motion.div>

        <h1 className="font-head text-white font-medium tracking-tighter mt-6 text-5xl sm:text-6xl lg:text-8xl leading-[0.95]">
          <MaskLine delay={0.15}>Designing the</MaskLine>
          <MaskLine delay={0.3}>Future of Living</MaskLine>
        </h1>

        {/* Brand tagline — mirrors the "─── LIVING ───" motif from the logo */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55, ease: easing }}
          data-testid="hero-brand-tagline"
          className="mt-8 inline-flex items-center gap-4 self-start"
        >
          <span className="h-px w-10 bg-[#B8935A]" />
          <span className="font-head italic tracking-[0.16em] text-[#B8935A] text-sm sm:text-base uppercase">
            Natural Spaces. Better Living.
          </span>
          <span className="h-px w-10 bg-[#B8935A]" />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease: easing }}
          className="mt-8 max-w-xl text-white/75 text-lg leading-relaxed"
        >
          Premium modular kitchens, wardrobes and complete home interiors — engineered with
          technology, delivered with craft, and finished on time.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: easing }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Cta to="/book-consultation" variant="light" data-testid="hero-book-cta">
            Book Free Design Consultation
          </Cta>
          <Cta to="/instant-quote" variant="ghost" data-testid="hero-quote-cta">
            Get Instant Quote
          </Cta>
          <Cta to="/portfolio" variant="ghost" data-testid="hero-portfolio-cta">
            View Portfolio
          </Cta>
        </motion.div>
      </div>
    </section>
  );
};

const Manifesto = () => (
  <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
      <div className="lg:col-span-5">
        <Reveal>
          <Overline>Our philosophy</Overline>
          <h2 className="font-head text-4xl sm:text-5xl font-medium tracking-tight mt-5 leading-tight">
            We build homes the way technology should — precisely, transparently, beautifully.
          </h2>
        </Reveal>
      </div>
      <div className="lg:col-span-6 lg:col-start-7 space-y-12">
        {MANIFESTO.map((m, i) => (
          <Reveal key={m.no} delay={i * 0.1}>
            <div className="flex gap-6 border-t border-black/10 pt-8">
              <div className="font-head text-xl text-[#1C1A17] font-semibold shrink-0">{m.no}</div>
              <div>
                <h3 className="text-xl font-semibold">{m.title}</h3>
                <p className="mt-3 text-[#5C574F] leading-relaxed">{m.body}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ContactSection = () => (
  <section id="contact" className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
      <div>
        <Reveal>
          <Overline>Contact</Overline>
          <h2 className="font-head text-4xl sm:text-5xl font-medium tracking-tight mt-5">
            Tell us about your dream home.
          </h2>
          <p className="mt-6 text-[#5C574F] leading-relaxed max-w-md">
            Fill in the form and our design team will reach out within 24 hours to schedule your
            free consultation.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-10">
          <LeadForm source="contact" ctaLabel="Send Message" />
        </Reveal>
      </div>
      <Reveal delay={0.1}>
        <a
          href="https://share.google/MeXgUV7q34s8mu1fS"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="home-map-link"
          className="group relative block h-full min-h-[420px] rounded-2xl overflow-hidden border border-black/10 bg-[#F5F2ED]"
          aria-label="Open Spherio Living on Google Maps"
        >
          <iframe
            title="Spherio Living Private Limited — Sushant Golf City, Lucknow"
            src="https://maps.google.com/maps?q=Spherio+Living+Private+Limited,+Sector+D,+Shopping+Square+1,+Sushant+Golf+City,+Lucknow+226030&z=15&output=embed"
            className="absolute inset-0 h-full w-full"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
          {/* Click-catcher so the whole card opens Google Maps in a new tab */}
          <span className="absolute inset-0 z-10" aria-hidden="true" />
          <div className="pointer-events-none absolute bottom-0 inset-x-0 z-20 bg-white/90 backdrop-blur px-6 py-5 flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-semibold">Spherio Living Office</div>
              <div className="text-sm text-[#5C574F] mt-1">SCO No. 222, Shopping Square 1, Sector D,<br/>Sushant Golf City, Lucknow 226030</div>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#1C1A17] px-3.5 py-2 text-xs font-semibold text-white transition-transform duration-300 group-hover:-translate-y-0.5 whitespace-nowrap">
              <MapPin className="h-3.5 w-3.5 text-[#B8935A]" />
              Directions
            </span>
          </div>
        </a>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Ribbon />
      <Manifesto />
      <ServicesGrid />
      <StatsBar dark />
      <WhyChoose />
      <ProcessSection />
      <Testimonials />
      <FaqSection />
      <ContactSection />
      <CtaBand />
    </>
  );
}
